import { useMutation } from '@tanstack/react-query';
import { useState, type SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  getAssertion,
  isCeremonyCancelled,
  isWebAuthnAvailable,
  securityKeySignInMutation,
  verifyMFAMutation,
  type MFAChallenge,
  type MFAMethod,
  type Session,
} from '@rootsight/shared';
import { Alert, Button, ErrorNotice, Stack } from '@rootsight/ui';

import { api } from '../../api';

import { CodeField } from './CodeField';

interface VerifyStepProps {
  challenge: MFAChallenge;
  onSession: (session: Session) => void;
  /** Back to the password, for a challenge that expired. */
  onRestart: () => void;
}

/** usableMethods are the challenge's methods this browser can use, in the
 * order offered: a security key first when the user has one. */
function usableMethods(challenge: MFAChallenge): MFAMethod[] {
  return challenge.methods.filter((m) => m !== 'webauthn' || isWebAuthnAvailable());
}

/**
 * VerifyStep asks for the second factor: a security key, the code from an
 * authenticator app, or a recovery code, whichever the user has, with a
 * switch between them.
 */
export function VerifyStep({ challenge, onSession, onRestart }: VerifyStepProps) {
  const { t } = useTranslation(['auth', 'common']);
  const methods = usableMethods(challenge);
  const [method, setMethod] = useState<MFAMethod>(methods[0] ?? 'recovery');
  const [code, setCode] = useState('');

  const verify = useMutation({ ...verifyMFAMutation(api), onSuccess: onSession });
  const key = useMutation({
    ...securityKeySignInMutation(api, getAssertion),
    onSuccess: onSession,
  });
  const failure = method === 'webauthn' ? key.error : verify.error;
  // The challenge lives five minutes; past that only the password helps.
  const expired = failure instanceof ApiError && failure.code === 'mfa_challenge_invalid';

  const submit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (method === 'webauthn') key.mutate({ mfaToken: challenge.mfaToken });
    else verify.mutate({ mfaToken: challenge.mfaToken, method, code });
  };
  const switchTo = (next: MFAMethod) => {
    verify.reset();
    key.reset();
    setCode('');
    setMethod(next);
  };
  const others = methods.filter((m) => m !== method);

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap={4}>
        <p>{t(`login.mfa.prompt.${method}`)}</p>
        {failure &&
          (isCeremonyCancelled(failure) ? (
            <Alert tone="info">{t('login.mfa.keyCancelled')}</Alert>
          ) : (
            <ErrorNotice error={failure} />
          ))}
        {method !== 'webauthn' && <CodeField kind={method} value={code} onChange={setCode} />}
        {expired ? (
          <Button variant="primary" size="lg" onPress={onRestart}>
            {t('login.mfa.startAgain')}
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isPending={verify.isPending || key.isPending}
          >
            {method === 'webauthn' ? t('login.mfa.useKeyNow') : t('login.mfa.verify')}
          </Button>
        )}
        {others.map((other) => (
          <Button key={other} variant="ghost" onPress={() => switchTo(other)}>
            {t(`login.mfa.switch.${other}`)}
          </Button>
        ))}
      </Stack>
    </form>
  );
}
